import React, { useState } from "react";
import { formatCurrency, parseBRL } from "../utils";
import { Category, FinancialEntry } from "@/types/finance";
import { MonthYear } from "@/types/monthYear";
import CurrencyInput from "./CurrencyInput";

interface TreeRowProps {
  category: Category;
  allCategories: Category[];
  monthYear: MonthYear;
  onUpdate: (category: Category, monthYear: MonthYear, amount: number) => void;
  monthlyValues: FinancialEntry[];
  level: number;
  calculateSum: (id: string) => number;
  getCategoryValue: (id: string) => number;
}

export const TreeRow: React.FC<TreeRowProps> = ({
  category,
  allCategories,
  monthYear,
  onUpdate,
  monthlyValues,
  level,
  calculateSum,
  getCategoryValue,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const children = allCategories.filter((c) => c.parentId === category.id);
  const isParent = children.length > 0;

  const displayValue = isParent
    ? calculateSum(category.id)
    : getCategoryValue(category.id);

  const startEditing = () => {
    if (isParent) return;
    setInputValue(
      displayValue > 0 ? displayValue.toString().replace(".", ",") : ""
    );
    setIsEditing(true);
  };

  const stopEditing = () => {
    setIsEditing(false);
    const amount = parseBRL(inputValue);
    onUpdate(category, monthYear, amount);
  };

  const newDisplayValue = () => {
    if (isParent) {
      return calculateSum(category.id);
    }
    return getCategoryValue(category.id);
  };

  return (
    <>
      <div
        className={`grid grid-cols-[1fr,150px] md:grid-cols-[1fr,200px] hover:bg-zinc-50/80 transition-all group ${
          isParent ? "bg-zinc-50/20" : ""
        }`}
      >
        <div
          className="p-4 flex items-center gap-3"
          style={{ paddingLeft: `${level * 24 + 24}px` }}
        >
          {isParent ? (
            <div className="w-5 h-5 flex items-center justify-center bg-white border border-zinc-200 rounded-md shadow-sm">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-3 h-3 text-zinc-400 transition-transform group-hover:rotate-90"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </div>
          ) : (
            <div
              className={`w-2 h-2 rounded-full ring-4 ${
                category.type === "income"
                  ? "bg-emerald-500 ring-emerald-50"
                  : "bg-rose-500 ring-rose-50"
              }`}
            ></div>
          )}
          <span
            className={`${
              isParent
                ? "font-black text-zinc-800 text-sm tracking-tight"
                : "text-zinc-600 text-sm font-medium"
            }`}
          >
            {category.name}
          </span>
        </div>

        <div className="p-4 text-right flex items-center justify-end pr-8">
          {isEditing ? (
            <div className="flex items-center gap-2 w-full justify-end animate-in fade-in zoom-in duration-200">
              <CurrencyInput
                valor={newDisplayValue()}
                onValorChange={(value) => setInputValue(value.toString())}
                onBlur={stopEditing}
                onKeyDown={(e) => e.key === "Enter" && stopEditing()}
              />
              {/* <input
                autoFocus
                type="text"
                className="w-full max-w-[120px] text-right bg-white border-2 border-[#3f83f8] rounded-xl px-3 py-1.5 outline-none text-[#3f83f8] font-black shadow-lg shadow-blue-50"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onBlur={stopEditing}
                onKeyDown={(e) => e.key === "Enter" && stopEditing()}
              /> */}
            </div>
          ) : (
            <div
              onClick={startEditing}
              className={`cursor-pointer px-4 py-2 rounded-xl transition-all text-right ${
                isParent
                  ? "font-black text-zinc-900 bg-zinc-100/50"
                  : "hover:bg-[#3f83f8] hover:text-white font-bold text-zinc-700 shadow-none hover:shadow-md"
              }`}
            >
              {formatCurrency(displayValue)}
            </div>
          )}
        </div>
      </div>

      {children.map((child) => (
        <TreeRow
          key={child.id}
          category={child}
          allCategories={allCategories}
          monthYear={monthYear}
          onUpdate={onUpdate}
          monthlyValues={monthlyValues}
          level={level + 1}
          calculateSum={calculateSum}
          getCategoryValue={getCategoryValue}
        />
      ))}
    </>
  );
};
